import { z, ZodError } from 'zod';
import { getJWT, removeJWT, setJWT } from '@/services/jwt';
import { getClient, getEventsClient } from '@goodneighbor/api/client';
import { ErrorResponse } from '@goodneighbor/api/schemas/error';
import {
  eventWithOrganizationSchema,
  EventsResponse
} from '@goodneighbor/api/schemas/events/index';
import { GetOrganizationResponse } from '@goodneighbor/api/routes/organizations/getOrganization/getOrganization.schema';
import { InferRequestType, InferResponseType } from 'hono/client';
import { isErrorResponse, isZodErrorResponse } from './typeguards';
import { registerForEventRequestSchema } from '@goodneighbor/api/routes/events/register/registerEvent.schema';
import { OrganizationEventRequest } from '@goodneighbor/api/routes/events/organization/registration/eventsOrganizationRegistration.schema';
import {
  deregisterEventParamsSchema,
  deregisterEventRequestBodySchema
} from '@goodneighbor/api/routes/events/attendance/deregister/deregisterEvent.schema';
import { deregisterResendParamsSchema } from '@goodneighbor/api/routes/events/attendance/deregister/resend/deregisterResend.schema';
import { postSupportRequestSchema } from '@goodneighbor/api/routes/organizations/supportRequest/supportRequest.schema';
import { createKindnessChallengeEntryRequestSchema } from '@goodneighbor/api/routes/kindness-challenges/kindnessChallenge.schema';
import {
  createPartnerInviteRequestSchema,
  createPartnerInviteResponseSchema
} from '@goodneighbor/api/routes/organization/invite/createPartnerInvite/createPartnerInvite.schema';
import {
  completePartnerInviteRequestSchema,
  completePartnerInviteResponseSchema
} from '@goodneighbor/api/routes/organization/invite/completePartnerInvite/completePartnerInvite.schema';
import { getTeamMembersResponseSchema } from '@goodneighbor/api/routes/organization/team/getTeamMembers/getTeamMembers.schema';

const getClientWithAuth = () =>
  getClient({
    headers: {
      Authorization: `Bearer ${typeof window !== 'undefined' ? getJWT() : ''}`
    }
  }).api['v1.0'];

const baseClient = getClient().api['v1.0'];
const baseClientv2 = getClient().api['v2.0'];

const handleError = (res: unknown) => {
  const errorResponse = res as unknown as ErrorResponse;
  const zodError: ZodError | undefined = isZodErrorResponse(errorResponse)
    ? (errorResponse.error as ZodError)
    : undefined;

  if (zodError && zodError.issues.length > 0 && zodError.issues[0]?.message) {
    throw new Error(zodError.issues[0].message);
  }

  if (errorResponse) {
    throw new Error(errorResponse.message);
  }
};

// ----------- Auth --------------
export type sendOtpBody = InferRequestType<
  (typeof baseClient.auth)['send-otp']['$post']
>['json'];

export const sendOtp = async (data: sendOtpBody) => {
  const res = await baseClient.auth['send-otp'].$post({
    json: data
  });
  const response = await res.json();
  if (!res.ok) {
    handleError(response);
  }
  if ('data' in response && response.data?.tokens?.accessToken) {
    setJWT(response.data.tokens.accessToken);
  }
  return response;
};

export type verifyOtpBody = InferRequestType<
  (typeof baseClient.auth)['verify-otp']['$post']
>['json'];

export const verifyOtp = async (data: verifyOtpBody) => {
  const authClient = getClientWithAuth();
  const res = await authClient.auth['verify-otp'].$post({
    json: data
  });
  const response = await res.json();
  if (!res.ok) {
    handleError(response);
  }
  if (
    response &&
    'data' in response &&
    response.data &&
    'tokens' in response.data
  ) {
    setJWT(response.data.tokens.accessToken);
  }
  return response;
};

export const signIn = async (
  data: InferRequestType<
    (typeof baseClient)['auth']['sign-in']['$post']
  >['json']
) => {
  const res = await baseClient.auth['sign-in'].$post({
    json: data
  });
  const response = await res.json();
  if (!res.ok || isErrorResponse(response)) {
    return handleError(response);
  }
  if (!('data' in response)) {
    return response;
  }
  setJWT(response.data.tokens.accessToken);
  return response;
};

export const verifyMfaLoginOtp = async (
  data: { otp: string },
  mfaToken: string
) => {
  const mfaClient = getClient({
    headers: { Authorization: `Bearer ${mfaToken}` }
  }).api['v1.0'];
  const res = await mfaClient.auth.mfa.verify.$post({ json: data });
  const response = await res.json();
  if (!res.ok || isErrorResponse(response)) {
    return handleError(response);
  }
  if ('data' in response && response.data?.tokens?.accessToken) {
    setJWT(response.data.tokens.accessToken);
  }
  return response;
};

export const resendMfaLoginOtp = async (mfaToken: string) => {
  const mfaClient = getClient({
    headers: { Authorization: `Bearer ${mfaToken}` }
  }).api['v1.0'];
  const res = await mfaClient.auth.mfa.resend.$post({});
  const response = await res.json();
  if (!res.ok || isErrorResponse(response)) {
    return handleError(response);
  }
  return response;
};

export const volunteerOtp = async (
  data: InferRequestType<
    (typeof baseClientv2)['auth']['signup']['verify']['$post']
  >['json']
) => {
  const res = await baseClientv2.auth.signup.verify.$post({
    json: data
  });
  const response = await res.json();
  if (!res.ok || isErrorResponse(response)) {
    return handleError(response);
  }
  return response;
};

export const signUp = async (
  data: InferRequestType<
    (typeof baseClientv2)['auth']['signup']['$post']
  >['json']
) => {
  const res = await baseClientv2.auth.signup.$post({
    json: data
  });
  const response = await res.json();
  if (!res.ok || isErrorResponse(response)) {
    return handleError(response);
  }
  setJWT(response.data.tokens.accessToken);
  return response;
};

export const forgotPassword = async (
  data: InferRequestType<(typeof baseClient)['auth']['forgot']['$post']>['json']
) => {
  const res = await baseClient.auth['forgot'].$post({
    json: data
  });
  const response = await res.json();
  if (!res.ok) {
    return handleError(response);
  }
  return response;
};

const verifyForgotPasswordPost =
  baseClient.auth.reset.otp[':email'][':otp'].$post;
export const verifyForgotPassword = async (
  params: InferRequestType<typeof verifyForgotPasswordPost>['param']
) => {
  const res = await verifyForgotPasswordPost({
    param: params
  });
  const response = await res.json();
  if (!res.ok || isErrorResponse(response)) {
    return handleError(response);
  }
  return response;
};

const resetPasswordPost = baseClient.auth['reset'][':email'][':token'].$post;
export const resetPassword = async (
  data: InferRequestType<typeof resetPasswordPost>['json'],
  params: InferRequestType<typeof resetPasswordPost>['param']
) => {
  const res = await resetPasswordPost({
    param: params,
    json: data
  });
  const response = await res.json();
  if (!res.ok) {
    return handleError(response);
  }
  return response;
};

export const sendMfaOtp = async (
  data: InferRequestType<
    ReturnType<typeof getClientWithAuth>['account']['mfa']['send-otp']['$post']
  >['json']
) => {
  const authClient = getClientWithAuth();
  const res = await authClient.account.mfa['send-otp'].$post({ json: data });
  const response = await res.json();
  if (!res.ok) {
    handleError(response);
  }
  return response;
};

export const verifyMfaOtp = async (
  data: InferRequestType<
    ReturnType<
      typeof getClientWithAuth
    >['account']['mfa']['verify-otp']['$post']
  >['json']
) => {
  const authClient = getClientWithAuth();
  const res = await authClient.account.mfa['verify-otp'].$post({
    json: data
  });
  const response = await res.json();
  if (!res.ok) {
    handleError(response);
  }
  return response;
};

export const disableMfa = async (
  data: InferRequestType<
    ReturnType<typeof getClientWithAuth>['account']['mfa']['disable']['$post']
  >['json']
) => {
  const authClient = getClientWithAuth();
  const res = await authClient.account.mfa.disable.$post({
    json: data
  });
  const response = await res.json();
  if (!res.ok) {
    handleError(response);
  }
  return response;
};

export const getAccount = async () => {
  const authClient = getClientWithAuth();
  const res = await authClient.account.$get();
  const response = await res.json();
  if (!res.ok || isErrorResponse(response)) {
    removeJWT();
    return handleError(response);
  }
  return response;
};

export const getAccountSetup = async (params: {
  email: string;
  token: string;
}) => {
  const res = await baseClient.account.setup.$get({
    query: params
  });
  const response = await res.json();
  if (!res.ok) {
    handleError(response);
  }
  return response;
};

export const resendSetupLink = async (email: string) => {
  const res = await baseClient.account.setup.resend.$post({
    json: { email }
  });
  const response = await res.json();
  if (!res.ok) {
    handleError(response);
  }
  return response;
};

export const putAccount = async (
  data: InferRequestType<typeof baseClient.account.$put>['json']
) => {
  const authClient = getClientWithAuth();
  const res = await authClient.account.$put({
    json: data
  });
  const response = await res.json();
  if (!res.ok || isErrorResponse(response)) {
    return handleError(response);
  }
  return response;
};

export type GetAccountEventsQuery = InferRequestType<
  typeof baseClient.account.events.$get
>['query'];

export const getAccountEvents = async (query: GetAccountEventsQuery) => {
  const authClient = getClientWithAuth();
  const res = await authClient.account.events.$get({
    query
  });
  const response = await res.json();
  if (!res.ok || isErrorResponse(response)) {
    return handleError(response);
  }
  return response;
};

// ----------- EVENTS --------------
export type EventsQuery = InferRequestType<
  typeof baseClient.events.$get
>['query'];

export const getEvent = async (id: string) => {
  const authClient = getClientWithAuth();
  const res = await authClient.events[':id'].$get({
    param: { id }
  });
  const response = await res.json();
  if (!res.ok) {
    handleError(response);
  }
  return response;
};

export const getEventRegistrationCount = async (id: string) => {
  const res = await getClient().api['v1.0'].events[':id'].register.count.$get({
    param: { id }
  });
  const response = await res.json();
  if (!res.ok) {
    handleError(response);
  }
  return response;
};

export const registerEvent = async (
  data: z.infer<typeof registerForEventRequestSchema> & { eventId: string }
) => {
  const authClient = getClientWithAuth();
  const res = await authClient.events[':id'].register.$post({
    param: { id: data.eventId },
    json: data
  });
  const response = await res.json();
  if (!res.ok) {
    handleError(response);
  }
  return response;
};

export const deregisterEvent = async (data: {
  params: z.infer<typeof deregisterEventParamsSchema>;
  body: z.infer<typeof deregisterEventRequestBodySchema>;
}) => {
  const authClient = getClientWithAuth();
  const res = await authClient.events.attendance[
    ':attendanceId'
  ].deregister.$post({
    param: data.params,
    json: data.body
  });
  const response = await res.json();
  if (!res.ok) {
    return handleError(response);
  }
  return response;
};

export const deregisterEventResend = async (data: {
  params: z.infer<typeof deregisterResendParamsSchema>;
}) => {
  const res = await baseClient.events.attendance[
    ':attendanceId'
  ].deregister.resend.$post({
    param: data.params
  });
  const response = await res.json();
  if (!res.ok) {
    return handleError(response);
  }
  return response;
};

export const getEvents = async (query: EventsQuery) => {
  const res = await getEventsClient().index.$get({
    query
  });
  const response = await res.json();
  if (!res.ok) {
    handleError(response);
  }
  return response as EventsResponse;
};

export type LocationsResponse = InferResponseType<
  (typeof baseClient.events)['locations']['$get'],
  200
>;

export const getLocations = async () => {
  const res = await baseClient.events.locations.$get();
  const response = await res.json();
  if (!res.ok) {
    handleError(response);
  }
  return response as LocationsResponse;
};

export type OrganizationVolunteersQuery = InferRequestType<
  (typeof baseClient.events)['organization']['volunteers']['$get']
>['query'];

export type OrganizationVolunteersResponse = InferResponseType<
  (typeof baseClient.events)['organization']['volunteers']['$get'],
  200
>;

export const getOrganizationVolunteers = async (
  query: OrganizationVolunteersQuery
) => {
  const authClient = getClientWithAuth();
  const res = await authClient.events.organization.volunteers.$get({
    query
  });

  if (!res.ok) {
    const errorResponse = await res.json();
    handleError(errorResponse);
    throw new Error('Failed to fetch organization volunteers');
  }

  const response = await res.json();
  return response as OrganizationVolunteersResponse;
};

export const getOrganizationSponsors = async (search?: string) => {
  const authClient = getClientWithAuth();
  const res = await authClient.organizations.sponsors.$get({
    query: { ...(search && { search }) }
  });

  if (!res.ok) {
    const errorResponse = await res.json();
    return handleError(errorResponse);
  }

  const response = await res.json();
  return response;
};

export type PutOrganizationBody = InferRequestType<
  typeof baseClient.organization.$put
>['json'];

export type PutOrganizationResponse = InferResponseType<
  typeof baseClient.organization.$put,
  200
>;

export const putOrganization = async (data: PutOrganizationBody) => {
  const authClient = getClientWithAuth();
  const res = await authClient.organization.$put({
    json: data
  });

  const response = await res.json();
  if (!res.ok || isErrorResponse(response)) {
    handleError(response);
  }
  return response as PutOrganizationResponse;
};

export const getOrganizationVolunteersCount = async () => {
  const authClient = getClientWithAuth();
  const res = await authClient.events.organization.volunteers.count.$get();

  const response = await res.json();
  if (!res.ok || isErrorResponse(response)) {
    return handleError(response);
  }
  return response;
};

export type EventWithOrganization = z.infer<typeof eventWithOrganizationSchema>;

export type ChallengesQuery = InferRequestType<
  typeof baseClient.challenges.$get
>['query'];

export const getChallenges = async (query: ChallengesQuery) => {
  const authClient = getClientWithAuth();
  const res = await authClient.challenges.$get({
    query
  });
  const response = await res.json();
  if (!res.ok) {
    handleError(response);
  }
  return response;
};

export type Challenges = InferResponseType<
  typeof baseClient.challenges.$get,
  200
>['data']['challenges'];

export type SendRegistrationEmailOtpBody = InferRequestType<
  (typeof baseClient.organizations.register)['send-email-otp']['$post']
>['json'];

export type SendRegistrationEmailOtpResponse = InferResponseType<
  (typeof baseClient.organizations.register)['send-email-otp']['$post']
>;

export const sendRegistrationEmailOtp = async (
  data: SendRegistrationEmailOtpBody
) => {
  const res = await baseClient.organizations.register['send-email-otp'].$post({
    json: data
  });

  const response = await res.json();
  if (!res.ok) {
    handleError(response);
  }
  return response as SendRegistrationEmailOtpResponse;
};

export type VerifyRegistrationEmailOtpBody = InferRequestType<
  (typeof baseClient.organizations.register)['verify-email-otp']['$post']
>['json'];

export type VerifyRegistrationEmailOtpResponse = InferResponseType<
  (typeof baseClient.organizations.register)['verify-email-otp']['$post']
>;

export const verifyRegistrationEmailOtp = async (
  data: VerifyRegistrationEmailOtpBody
) => {
  const res = await baseClient.organizations.register['verify-email-otp'].$post(
    {
      json: data
    }
  );

  const response = await res.json();
  if (!res.ok) {
    handleError(response);
  }
  return response as VerifyRegistrationEmailOtpResponse;
};

export type CheckSlugQuery = InferRequestType<
  (typeof baseClient.organizations)['check-slug']['$get']
>['query'];

export type CheckSlugResponse = InferResponseType<
  (typeof baseClient.organizations)['check-slug']['$get'],
  200
>;

export const checkSlugAvailability = async (
  slug: string
): Promise<CheckSlugResponse> => {
  const res = await baseClient.organizations['check-slug'].$get({
    query: { slug }
  });

  const response = await res.json();
  if (!res.ok) {
    handleError(response);
  }
  return response as CheckSlugResponse;
};

export const postEventOrganizationRegistration = async (
  data: OrganizationEventRequest
) => {
  const authClient = getClientWithAuth();
  const res = await authClient.events.organization.registration.$post({
    json: data
  });

  const response = await res.json();
  if (!res.ok || isErrorResponse(response)) {
    handleError(response);
  }
  return response;
};

export const getEventOrganizationCount = async () => {
  const authClient = getClientWithAuth();
  const res = await authClient.events.organization.count.$get();

  const response = await res.json();
  if (!res.ok || isErrorResponse(response)) {
    return handleError(response);
  }
  return response;
};

export const getEventsOrganization = async (query: EventsQuery) => {
  const authClient = getClientWithAuth();
  const res = await authClient.events.organization.$get({
    query
  });

  const response = await res.json();
  if (!res.ok || isErrorResponse(response)) {
    return handleError(response);
  }
  return response;
};

export type OrganizationEventSponsorsQuery = InferRequestType<
  (typeof baseClient.events)['organization'][':id']['sponsors']['$get']
>['query'];

export const getOrganizationEventSponsors = async (
  eventId: string,
  query: OrganizationEventSponsorsQuery
) => {
  const authClient = getClientWithAuth();
  const res = await authClient.events.organization[':id'].sponsors.$get({
    param: { id: eventId },
    query
  });

  const response = await res.json();
  if (!res.ok || isErrorResponse(response)) {
    return handleError(response);
  }

  return response;
};

export type OrganizationEventVolunteersQuery = InferRequestType<
  (typeof baseClient.events)['organization'][':id']['volunteers']['$get']
>['query'];

export const getOrganizationEventVolunteers = async (
  eventId: string,
  query: OrganizationEventVolunteersQuery
) => {
  const authClient = getClientWithAuth();
  const res = await authClient.events.organization[':id'].volunteers.$get({
    param: { id: eventId },
    query
  });

  const response = await res.json();
  if (!res.ok || isErrorResponse(response)) {
    return handleError(response);
  }

  return response;
};

export const getOrganizationEventVolunteersCsv = async (
  eventId: string,
  query?: { page?: string; perPage?: string; search?: string }
) => {
  const authClient = getClientWithAuth();
  const res = await authClient.events.organization[':id'].volunteers.csv.$get({
    param: { id: eventId },
    query: query ?? {}
  });

  const contentType = res.headers.get('content-type');

  if (!res.ok || contentType !== 'text/csv') {
    const response = await res.json();
    return handleError(response);
  }

  return {
    blob: await res.blob(),
    fileName: res.headers.get('Content-Disposition')?.split('filename=')[1]
  };
};

export const getEventOrganizationById = async (id: string) => {
  const authClient = getClientWithAuth();
  const res = await authClient.events.organization[':id'].$get({
    param: { id }
  });

  const response = await res.json();
  if (!res.ok || isErrorResponse(response)) {
    return handleError(response);
  }
  return response;
};

export const getOrganization = async (slug: string) => {
  const res = await baseClient.organizations[':slug'].$get({
    param: { slug }
  });

  if (res.status === 404) {
    return null;
  }

  const response = await res.json();
  if (!res.ok) {
    handleError(response);
  }

  return response as GetOrganizationResponse;
};

type OrgainzationPresignedUploadUrlBody = InferRequestType<
  (typeof baseClient.organizations.uploads)['create-presigned-url']['$post']
>['json'];

export const organizationPresignedUploadUrl = async (
  data: OrgainzationPresignedUploadUrlBody
) => {
  const res = await baseClient.organizations.uploads[
    'create-presigned-url'
  ].$post({
    json: data
  });

  const response = await res.json();
  if (!res.ok || isErrorResponse(response)) {
    return handleError(response);
  }
  return response;
};

export const uploadOrganizationFile = async (
  file: File,
  namespace: 'avatar' = 'avatar'
): Promise<string> => {
  const presignedResponse = await organizationPresignedUploadUrl({
    fileName: file.name,
    fileType: file.type,
    size: file.size,
    namespace
  });

  if (!presignedResponse || !('url' in presignedResponse)) {
    throw new Error('Failed to get presigned URL');
  }

  const uploadResponse = await fetch(presignedResponse.url, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type
    }
  });

  if (!uploadResponse.ok) {
    throw new Error('Failed to upload file to S3');
  }

  return presignedResponse.uploadId;
};

export const registerVolunteerForEventAsPartner = async (
  data: z.infer<typeof registerForEventRequestSchema> & { eventId: string }
) => {
  const authClient = getClientWithAuth();
  const res = await authClient.organizations['register-volunteer'][':id'].$post(
    {
      param: { id: data.eventId },
      json: data
    }
  );
  const response = await res.json();
  if (!res.ok) {
    handleError(response);
  }
  return response;
};

export const partnerSupportRequest = async (
  data: z.infer<typeof postSupportRequestSchema>
) => {
  const authClient = getClientWithAuth();
  const res = await authClient.organizations.support.$post({
    json: data
  });
  const response = await res.json();
  if (!res.ok) {
    return handleError(response);
  }
  return response;
};

export const partnerBulkCheckin = async (
  eventId: string,
  attendanceIds: string[]
) => {
  const authClient = getClientWithAuth();
  const res = await authClient.events.organization[':id'].checkin.$post({
    param: { id: eventId },
    json: { attendanceIds }
  });
  const response = await res.json();
  if (!res.ok || isErrorResponse(response)) {
    return handleError(response);
  }
  return response;
};

export const partnerUpdateVolunteerStatus = async (
  eventId: string,
  attendanceId: string,
  status: 'Registered' | 'Cancelled' | 'Checked-In'
) => {
  const authClient = getClientWithAuth();
  const res = await authClient.events.organization[':id'].volunteers[
    ':attendanceId'
  ].status.$patch({
    param: { id: eventId, attendanceId },
    json: { status }
  });
  const response = await res.json();
  if (!res.ok || isErrorResponse(response)) {
    return handleError(response);
  }
  return response;
};

export const getFlags = async () => {
  const authClient = getClientWithAuth();
  const res = await authClient.account.flags.$get();
  const response = await res.json();
  if (!res.ok || isErrorResponse(response)) {
    return handleError(response);
  }
  return response;
};

export const createKindnessChallengeEntry = async (
  data: z.infer<typeof createKindnessChallengeEntryRequestSchema>
) => {
  const res = await baseClient['kindness-challenges'].entries.$post({
    json: data
  });
  const response = await res.json();
  if (!res.ok) {
    handleError(response);
  }
  return response;
};

export const getKindnessChallenges = async () => {
  const res = await baseClient['kindness-challenges'].$get();
  if (!res.ok) {
    const errorBody = await res.json();
    handleError(errorBody);
  }
  const response = await res.json();
  return response as { slug: string; displayName: string }[];
};

export type CreatePartnerInviteBody = z.infer<
  typeof createPartnerInviteRequestSchema
>;

export type CreatePartnerInviteResponse = z.infer<
  typeof createPartnerInviteResponseSchema
>;

export const createPartnerInvite = async (data: CreatePartnerInviteBody) => {
  const authClient = getClientWithAuth();
  const res = await authClient.organization.invites.$post({ json: data });
  const response = await res.json();
  if (!res.ok || isErrorResponse(response)) {
    return handleError(response);
  }
  return response;
};

export type CompletePartnerInviteBody = z.infer<
  typeof completePartnerInviteRequestSchema
>;

export type CompletePartnerInviteResponse = z.infer<
  typeof completePartnerInviteResponseSchema
>;

export const completePartnerInvite = async (
  id: string,
  data: CompletePartnerInviteBody
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const res = await (baseClient.organization.invites as any)[
    ':id'
  ].complete.$post({
    param: { id },
    json: data
  });
  const response = await res.json();
  if (!res.ok || isErrorResponse(response)) {
    return handleError(response);
  }
  if ('data' in response && response.data?.tokens?.accessToken) {
    setJWT(response.data.tokens.accessToken);
  }
  return response as CompletePartnerInviteResponse;
};

export type GetTeamMembersResponse = z.infer<
  typeof getTeamMembersResponseSchema
>;

export const getTeamMembers = async () => {
  const authClient = getClientWithAuth();
  const res = await authClient.organization.team.$get();
  const response = await res.json();
  if (!res.ok || isErrorResponse(response)) {
    return handleError(response);
  }
  return response as GetTeamMembersResponse;
};

export const updateTeamMemberRole = async (
  userId: string,
  roleName: string
) => {
  const authClient = getClientWithAuth();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const res = await (authClient.organization.team as any)[
    ':userId'
  ].role.$patch({
    param: { userId },
    json: { roleName }
  });
  const response = await res.json();
  if (!res.ok || isErrorResponse(response)) {
    return handleError(response);
  }
  return response;
};
